/**
 * SD Shipping Restriction
 *
 * Hides buy buttons for products flagged with state shipping restrictions
 * when the user is in a restricted US state.
 *
 * User location is resolved by the Ketch location resolver (in scripts.liquid)
 * and stamped on <body data-sd-user-country="US" data-sd-user-state="XX">.
 * Currently gates on country === 'US'; extensible for future country support.
 *
 * Fail-open: if location can't be determined, buttons remain enabled.
 */

const STATE_NAME_TO_CODE = {
  ALABAMA: 'AL',
  ALASKA: 'AK',
  ARIZONA: 'AZ',
  ARKANSAS: 'AR',
  CALIFORNIA: 'CA',
  COLORADO: 'CO',
  CONNECTICUT: 'CT',
  DELAWARE: 'DE',
  FLORIDA: 'FL',
  GEORGIA: 'GA',
  HAWAII: 'HI',
  IDAHO: 'ID',
  ILLINOIS: 'IL',
  INDIANA: 'IN',
  IOWA: 'IA',
  KANSAS: 'KS',
  KENTUCKY: 'KY',
  LOUISIANA: 'LA',
  MAINE: 'ME',
  MARYLAND: 'MD',
  MASSACHUSETTS: 'MA',
  MICHIGAN: 'MI',
  MINNESOTA: 'MN',
  MISSISSIPPI: 'MS',
  MISSOURI: 'MO',
  MONTANA: 'MT',
  NEBRASKA: 'NE',
  NEVADA: 'NV',
  'NEW HAMPSHIRE': 'NH',
  'NEW JERSEY': 'NJ',
  'NEW MEXICO': 'NM',
  'NEW YORK': 'NY',
  'NORTH CAROLINA': 'NC',
  'NORTH DAKOTA': 'ND',
  OHIO: 'OH',
  OKLAHOMA: 'OK',
  OREGON: 'OR',
  PENNSYLVANIA: 'PA',
  'RHODE ISLAND': 'RI',
  'SOUTH CAROLINA': 'SC',
  'SOUTH DAKOTA': 'SD',
  TENNESSEE: 'TN',
  TEXAS: 'TX',
  UTAH: 'UT',
  VERMONT: 'VT',
  VIRGINIA: 'VA',
  WASHINGTON: 'WA',
  'WEST VIRGINIA': 'WV',
  WISCONSIN: 'WI',
  WYOMING: 'WY',
  'DISTRICT OF COLUMBIA': 'DC',
};

/**
 * Normalize a single state input to a 2-letter code.
 * Accepts: "CA", "california", "California", etc.
 * @param {string} input
 * @returns {string|null}
 */
function normalizeStateInput(input) {
  const trimmed = input.trim().toUpperCase();
  if (!trimmed) return null;
  if (trimmed.length === 2 && /^[A-Z]{2}$/.test(trimmed)) return trimmed;
  return STATE_NAME_TO_CODE[trimmed] || null;
}

/**
 * Parse the comma-separated restricted states setting into a Set of 2-letter codes.
 * @param {string} raw
 * @returns {Set<string>}
 */
function parseRestrictedStates(raw) {
  const codes = new Set();
  if (!raw) return codes;
  for (const part of raw.split(',')) {
    const code = normalizeStateInput(part);
    if (code) codes.add(code);
  }
  return codes;
}

/**
 * Get user location from body data attributes (set by Ketch resolver in scripts.liquid).
 * If not yet stamped, waits for the sd:user-location-resolved event.
 * @returns {Promise<{country: string, state: string}|null>}
 */
function getUserLocation() {
  const country = document.body.dataset.sdUserCountry;
  const state = document.body.dataset.sdUserState;
  // If country is stamped, the resolver has finished — return immediately.
  // state may be empty for non-US visitors; init() handles filtering.
  if (country) return Promise.resolve({ country, state: state || null });

  // Otherwise wait for the resolver to stamp values via custom event.
  // This handles the race where the inline resolver defers to DOMContentLoaded
  // (body not yet available when script runs in <head>) while this module
  // executes after parsing but before DOMContentLoaded.
  return new Promise((resolve) => {
    let settled = false;
    function settle(value) {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      resolve(value);
    }

    const timeout = setTimeout(() => settle(null), 12000);
    document.body.addEventListener(
      'sd:user-location-resolved',
      (e) => {
        const c = e.detail?.country;
        settle(c ? { country: c, state: e.detail?.state || null } : null);
      },
      { once: true }
    );

    // Re-check after registering the listener to close the window where
    // the event fires between the initial check and addEventListener.
    const recheckCountry = document.body.dataset.sdUserCountry;
    if (recheckCountry) {
      settle({ country: recheckCountry, state: document.body.dataset.sdUserState || null });
    }
  });
}

/**
 * Apply restriction to a single buy-buttons block element.
 * @param {HTMLElement} block
 * @param {string} userState - 2-letter state code
 */
function applyRestriction(block, userState) {
  const rawStates = block.dataset.sdRestrictedStates || '';
  const restrictedStates = parseRestrictedStates(rawStates);

  if (!restrictedStates.has(userState)) return;

  // Already restricted (e.g. re-run after morph)
  if (block.dataset.sdRestricted === 'true') return;

  const message = block.dataset.sdRestrictionMessage || 'Not Available in Your State';

  // Hide the product form buttons container
  const formButtons = block.querySelector('.product-form-buttons');
  if (formButtons) formButtons.hidden = true;

  // Hide accelerated checkout if present (may be outside .product-form-buttons)
  const accelerated = block.querySelector('shopify-accelerated-checkout');
  if (accelerated) accelerated.hidden = true;

  // Append the restriction button.
  // Use aria-disabled instead of native disabled so the button remains
  // in the tab order and is discoverable by screen reader users.
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.setAttribute('aria-disabled', 'true');
  btn.className = 'sd-shipping-restriction-btn';
  btn.textContent = message;

  // Insert after the product-form-component (or at end of block)
  const formComponent = block.querySelector('product-form-component');
  if (formComponent) {
    formComponent.after(btn);
  } else {
    block.appendChild(btn);
  }

  // Announce the restriction to screen readers via the existing live region
  const liveRegion = block.querySelector('[aria-live]');
  if (liveRegion) liveRegion.textContent = message;

  // Hide sibling local-pickup element
  const localPickup = block.parentElement?.querySelector('local-pickup');
  if (localPickup) localPickup.hidden = true;

  block.dataset.sdRestricted = 'true';
}

/**
 * Process all unprocessed restricted blocks on the page.
 * @param {string} userState - 2-letter state code
 */
function processBlocks(userState) {
  const blocks = document.querySelectorAll('[data-sd-shipping-restricted="true"]:not([data-sd-restricted="true"])');
  for (const block of blocks) {
    applyRestriction(block, userState);
  }
}

/**
 * Initialize: process existing blocks and listen for quick-add modal content.
 */
async function init() {
  const location = await getUserLocation();
  if (!location) return; // Fail-open

  // Currently restricted to US only. Future country support can add
  // additional checks here without changing the resolver or DOM contract.
  if (location.country !== 'US' || !location.state) return;

  const userState = location.state;

  // Process any blocks already in the DOM (product pages)
  processBlocks(userState);

  // Listen for the quick-add lifecycle event dispatched by sd-quick-add.js
  // after morph() + post-morph reinitialize calls complete.
  document.addEventListener('sd:quick-add-content-loaded', () => {
    processBlocks(userState);
  });

  // Observe the modal content element for attribute and structural changes.
  // Variant-update morphs redo the buy-buttons content asynchronously,
  // removing our injected restriction. This observer reapplies it.
  // Debounce with rAF to coalesce rapid morph mutations into one pass.
  const modalContent = document.getElementById('sd-quick-add-modal-content');
  if (modalContent) {
    let pending = false;
    new MutationObserver(() => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        processBlocks(userState);
        pending = false;
      });
    }).observe(modalContent, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-sd-shipping-restricted'],
    });
  }
}

init();
